/**
 * Sfx (DESIGN 6.8, CROSS-PACKAGE CONTRACTS): turns queue feedback and
 * GameEvents into sounds, panned by x offset from the local player, with
 * mute and levels remembered (GameAudio prefs).
 *
 * Every sound has a name from audio/names.ts. If the manifest has a file
 * for it the recording plays (GameAudio); if not, the synth recipe in
 * sfxPatches.ts plays instead, so a missing file is never silence.
 *
 * Contract surface: `resume()` on the first gesture, `onEvent(ev, vm)` per
 * event, `onQueue(outcome, el)` when the local queue changed, `setMuted` /
 * `muted`, `ui(kind)` for menu presses. `onFrame(vm, dt)` drives what has
 * no event of its own: the beam hum, the burn crackle, the boulder charge,
 * enemy attack tells, spawns, the local player's footsteps, and the music
 * and ambience beds that follow the room phase.
 *
 * Throttles keep the mix legible: 10 Hz beam ticks collapse to one hit
 * sound per damage type per 70 ms, status pings repeat per target at most
 * every 250 ms, and the self-shock buzz plays once even though both the
 * predicted queue and the server event report it.
 */

import type { Element, GameEvent, PushOutcome, Vec2 } from '@shared/index.js';
import { BARRIER, isBossWave } from '@shared/index.js';
import type { SOUND_SPRAY_ELEMENTS, SOUND_STORM_ELEMENTS } from './names.js';
import type { QueueFeedbackOutcome, ViewModel } from '../state/viewModel.js';
import { MANIFEST_URL, getAudio, type GameAudio, type SampleLoop } from './audio.js';
import {
  ELEMENT_FREQ, bossSound, boltSound, cancelBlip, castSound, deathSound, degradeBlip, elementPress, explosionSound, fizzle, fullClick,
  healSound, hitSound, leadElement, magickSound, mergeSound, oopsSound, reviveSound, selfShockBuzz, statusSound, waveSound,
} from './sfxPatches.js';
import { type LoopHandle, type Synth, clampPan, getSynth } from './synth.js';

/** World units of x offset that map to full left / right. */
const PAN_UNITS = 12;
const HIT_THROTTLE_MS = 70;
const STATUS_THROTTLE_MS = 250;
const SHOCK_THROTTLE_MS = 300;
const MERGE_THROTTLE_MS = 400;
const OOPS_THROTTLE_MS = 1200;
const TELL_THROTTLE_MS = 400;
const SPAWN_THROTTLE_MS = 120;
/** World units walked between footsteps. */
const STEP_UNITS = 1.4;
const BEAM_GAIN = 0.12;
const REMOTE_BEAM_GAIN = 0.05;
const BURN_GAIN = 0.1;
const CHARGE_GAIN_MIN = 0.05;
const CHARGE_GAIN_MAX = 0.14;
const CHARGE_FREQ_MIN = 110;
const CHARGE_FREQ_MAX = 440;
/** Waves at or past this get the busier bed; every fifth wave is the boss's and has its own (isBossWave). */
const HIGH_WAVE = 5;
/** The defeat sting is 9 s; the results bed takes over once it has faded. */
const RESULTS_AFTER_MS = 9500;
const MINE_ARM_THROTTLE_MS = 90;
const WALL_BREAK_THROTTLE_MS = 150;
/** A storm wall is heard within this many units. */
const STORM_HEAR_UNITS = 14;
/** Each lead element's nova and storm recordings (the fused and odd ones borrow the nearest). */
const NOVA_SOUND: Readonly<Record<Element, string | null>> = {
  fire: 'cast.nova.fire', lava: 'cast.nova.fire', cold: 'cast.nova.cold', ice: 'cast.nova.cold', water: 'cast.nova.water', steam: 'cast.nova.water',
  mud: 'cast.nova.water', spring: 'cast.nova.life', lightning: 'cast.nova.lightning', arcane: 'cast.nova.arcane', life: 'cast.nova.life',
  earth: null, shield: null,
  // Woven pieces borrow their nearest parent's nova.
  plasma: 'cast.nova.lightning', flux: 'cast.nova.arcane', stillness: 'cast.nova.cold', obsidian: null, ether: 'cast.nova.arcane',
  cloud: 'cast.nova.water', ash: 'cast.nova.fire', rime: 'cast.nova.cold', moment: 'cast.nova.arcane', dragonfire: 'cast.nova.fire',
  stormglass: 'cast.nova.lightning', thunderhead: 'cast.nova.lightning', time: 'cast.nova.arcane',
  tide: 'cast.nova.water', pulse: 'cast.nova.life', void: 'cast.nova.arcane', wildfire: 'cast.nova.fire', whirlpool: 'cast.nova.water',
  deluge: 'cast.nova.water', inferno: 'cast.nova.fire', glacier: 'cast.nova.cold', soul: 'cast.nova.life', sun: 'cast.nova.fire',
  winter: 'cast.nova.cold', death: 'cast.nova.arcane',
};
const STORM_SOUND: Readonly<Record<Element, (typeof SOUND_STORM_ELEMENTS)[number]>> = {
  fire: 'fire', lava: 'fire', cold: 'cold', ice: 'cold', water: 'water', mud: 'water', spring: 'life', life: 'life',
  lightning: 'lightning', steam: 'steam', arcane: 'arcane', earth: 'arcane', shield: 'arcane',
  plasma: 'lightning', flux: 'arcane', stillness: 'cold', obsidian: 'arcane', ether: 'arcane', cloud: 'water', ash: 'fire', rime: 'cold',
  moment: 'arcane', dragonfire: 'fire', stormglass: 'lightning', thunderhead: 'lightning', time: 'arcane',
  tide: 'water', pulse: 'life', void: 'arcane', wildfire: 'fire', whirlpool: 'water', deluge: 'water', inferno: 'fire', glacier: 'cold',
  soul: 'life', sun: 'fire', winter: 'cold', death: 'arcane',
};
/** Each lead element's spray sustain (lightning arcs instead; arcane and shield have none). */
const SPRAY_SOUND: Readonly<Record<Element, (typeof SOUND_SPRAY_ELEMENTS)[number] | null>> = {
  water: 'water', fire: 'fire', cold: 'cold', ice: 'cold', steam: 'steam', mud: 'mud', earth: 'mud', lava: 'lava',
  spring: 'spring', life: 'spring', lightning: null, arcane: null, shield: null,
  // Woven pieces borrow their nearest parent's sustain.
  plasma: 'steam', flux: null, stillness: 'cold', obsidian: 'lava', ether: null, cloud: 'water', ash: 'fire', rime: 'cold',
  moment: null, dragonfire: 'fire', stormglass: 'cold', thunderhead: 'steam', time: null,
  tide: 'water', pulse: null, void: null, wildfire: 'fire', whirlpool: 'water', deluge: 'water', inferno: 'lava', glacier: 'cold',
  soul: null, sun: 'fire', winter: 'cold', death: null,
};
/** A spray or an arc is heard within this many units; the caster's own always. */
const CHANNEL_HEAR_UNITS = 12;

/** A synth loop or a looping recording: both answer setGain / setPan / stop. */
type LoopVoice = LoopHandle | SampleLoop;

function isSample(v: LoopVoice): v is SampleLoop {
  return 'setRate' in v;
}

/** Story mode: 1 while anything is left to fight (the fight layer up), 0 when the way is clear. */
function fightMix(vm: ViewModel): number {
  return vm.wave && vm.wave.alive > 0 ? 1 : 0;
}

export class Sfx {
  private readonly synth: Synth;
  private readonly audio: GameAudio;
  private me: Vec2 | null = null;
  private beamLoop: LoopVoice | null = null;
  private beamElement: Element | null = null;
  private remoteBeamLoop: LoopVoice | null = null;
  private remoteBeamElement: Element | null = null;
  private burnLoop: LoopVoice | null = null;
  private chargeLoop: LoopVoice | null = null;
  private tornadoLoop: LoopVoice | null = null;
  /** Time Stop's slow clock (a recording only: no synth stand-in). */
  private timestopLoop: SampleLoop | null = null;
  private boilerLoop: LoopVoice | null = null;
  private stormLoop: SampleLoop | null = null;
  private stormSound: string | null = null;
  private sprayLoop: SampleLoop | null = null;
  private spraySound: string | null = null;
  private arcLoop: SampleLoop | null = null;
  /** Shields, walls and mines as last seen (updateHazards), and each wizard's bubble. */
  private readonly hazardsSeen = new Map<number, { k: string; ice: boolean; frac: number; x: number; y: number; frame: number }>();
  private hazardFrame = 0;
  private hazardsPrimed = false;
  private readonly bubbleSeen = new Map<number, number>();
  /** The ViewModel.epoch last heard. */
  private epoch = 0;
  /** Mother Mire is on the field (story): her music instead of the act's layers. */
  private storyBoss = false;
  /** The boss has enraged this wave: its second-phase bed plays on to the wave's end. */
  private raged = false;
  /** Story mode: the act 1 layers' current mix (fightMix). */
  private storyMix = 0;
  private readonly lastAt = new Map<string, number>();
  private enemyState = new Map<number, string>();
  private projectilesSeen = new Set<number>();
  private lastPos: Vec2 | null = null;
  private walked = 0;
  private waveN = 0;
  private phase: ViewModel['phase'] | null = null;
  private resultsTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(synth: Synth = getSynth(), audio: GameAudio = getAudio()) {
    this.synth = synth;
    this.audio = audio;
    this.audio.use(MANIFEST_URL);
  }

  get muted(): boolean {
    return this.audio.muted;
  }

  /** Call from the first user gesture: opens the AudioContext (browsers block audio before a gesture). */
  resume(): void {
    this.audio.unlock();
  }

  setMuted(m: boolean): void {
    this.audio.setMuted(m);
    if (m) this.stopLoops();
  }

  /** A menu press, open, close, ready or chat ping. */
  ui(kind: 'click' | 'open' | 'close' | 'ready' | 'chat'): void {
    if (!this.live()) return;
    if (this.audio.play(`ui.${kind}`, { vol: 0.6, throttleMs: 30 })) return;
    if (kind === 'click' || kind === 'open' || kind === 'close') this.synth.tone({ freq: kind === 'close' ? 520 : 660, dur: 0.05, type: 'square', gain: 0.05, lowpass: 2500 });
    else this.synth.chord([880, 1320], { dur: 0.12, type: 'sine', gain: 0.06 });
  }

  /** The recording for `name`, when there is one; false means "play the synth". */
  private s(name: string, pan = 0, vol = 1, pitch = 1, throttleMs?: number): boolean {
    const opts: { pan: number; vol: number; pitch: number; throttleMs?: number } = { pan, vol, pitch };
    if (throttleMs !== undefined) opts.throttleMs = throttleMs;
    return this.audio.play(name, opts);
  }

  /** A little pitch spread so repeats of one recording do not sound like a machine gun. */
  private spread(): number {
    return 0.94 + Math.random() * 0.12;
  }

  /** Local queue feedback (DESIGN 6.7): one distinct tone per outcome. */
  onQueue(outcome: PushOutcome | QueueFeedbackOutcome, el: Element | null): void {
    if (!this.live()) return;
    const pan = 0;
    switch (outcome) {
      case 'added':
        if (el && !this.s(`queue.${el}`)) elementPress(this.synth, el, pan);
        break;
      case 'combined': {
        // `el` is the fused element (steam / ice / mud / lava / spring): the glide-into-one sound.
        const fused = el ?? 'steam';
        if (!this.s(`queue.${fused}`)) elementPress(this.synth, fused, pan);
        break;
      }
      case 'degraded':
        if (!this.s('queue.degrade')) degradeBlip(this.synth, pan);
        break;
      case 'cancelled':
        if (!this.s('queue.cancel')) cancelBlip(this.synth, pan);
        break;
      case 'full':
        if (!this.s('queue.full')) fullClick(this.synth, pan);
        break;
      case 'self_shock':
        if (this.allow('shock', SHOCK_THROTTLE_MS) && !this.s('shock.self')) selfShockBuzz(this.synth, pan);
        break;
      case 'fizzle':
        if (!this.s('queue.fizzle')) fizzle(this.synth, pan);
        break;
      case 'magick_wait':
        // the Magick is cooling down: the same dull click as a full queue, nothing cast
        if (!this.s('queue.full')) fullClick(this.synth, pan);
        break;
    }
  }

  /** One GameEvent from this frame's vm.events; `vm` supplies positions for panning. */
  onEvent(ev: GameEvent, vm: ViewModel): void {
    this.me = vm.me && vm.me.x !== undefined && vm.me.y !== undefined ? { x: vm.me.x, y: vm.me.y } : this.me;
    if (ev.e === 'wave') this.onPhase(ev.phase, vm);
    if (!this.live()) return;
    const pid = vm.me?.pid ?? -1;
    const s = this.synth;
    switch (ev.e) {
      case 'cast': {
        const pan = this.panAt(ev.x);
        // a nova in its element's own burst, an ice wall in its own crackle, ice shards their glassy whoosh; the rest by kind
        const own = ev.kind === 'nova' && ev.el.length > 0 ? NOVA_SOUND[leadElement(ev.el)]
          : ev.kind === 'wall' && ev.el.includes('ice') ? 'cast.wall.ice'
            : ev.kind === 'projectile' && ev.el.includes('ice') && !ev.el.includes('earth') ? 'cast.projectile.ice' : null;
        if (own && this.s(own, pan, 1, this.spread())) break;
        const kind = ev.kind === 'bubble' ? 'barrier' : ev.kind;
        const name = ev.kind === 'projectile' && ev.el.includes('earth') ? 'cast.projectile.earth' : `cast.${kind}`;
        if (!this.s(name, pan, 1, this.spread()) && !(name !== `cast.${kind}` && this.s(`cast.${kind}`, pan, 1, this.spread()))) castSound(s, ev.kind, ev.el, pan);
        break;
      }
      case 'hit':
        if (this.allow(`hit:${ev.type}`, HIT_THROTTLE_MS)) {
          const pan = this.panAt(ev.x);
          if (this.s(`hit.${ev.type}`, pan, 1, this.spread())) {
            if (ev.crit === 1 && !this.s('hit.crit', pan)) s.tone({ freq: 1760, dur: 0.08, type: 'sine', gain: 0.1, pan });
          } else hitSound(s, ev.type, ev.crit === 1, pan);
        }
        break;
      case 'heal':
        if (this.allow(`heal:${ev.tgt}`, STATUS_THROTTLE_MS)) {
          const pan = this.panOf(ev.tgt, vm);
          if (!this.s('heal', pan)) healSound(s, pan);
        }
        break;
      case 'status':
        if (ev.on === 1 && this.allow(`status:${ev.id}:${ev.tgt}`, STATUS_THROTTLE_MS)) {
          const pan = this.panOf(ev.tgt, vm);
          if (!this.s(`status.${ev.id}`, pan)) {
            if (ev.id === 'mired') s.noise({ dur: 0.25, filter: 'lowpass', freq: 320, freqTo: 70, gain: 0.35, pan });
            else statusSound(s, ev.id, pan);
          }
        } else if (ev.on === 0 && ev.id === 'frozen' && this.allow(`thaw:${ev.tgt}`, STATUS_THROTTLE_MS)) {
          // the ice block cracks open
          this.s('status.thaw', this.panOf(ev.tgt, vm), 1, this.spread());
        }
        break;
      case 'death': {
        const player = vm.players.some((p) => p.id === ev.tgt) || vm.corpses.some((c) => c.id === ev.tgt);
        const pan = this.panOf(ev.tgt, vm);
        if (!this.s(player ? 'death.player' : 'death.enemy', pan, 1, this.spread())) deathSound(s, player, pan);
        break;
      }
      case 'revive': {
        const pan = this.panOf(ev.tgt, vm);
        if (!this.s('revive', pan)) reviveSound(s, pan);
        break;
      }
      case 'explosion': {
        const pan = this.panAt(ev.x);
        const size = Math.max(0.5, Math.min(1.4, ev.r / 2.5));
        if (!this.s(`boom.${ev.kind}`, pan, Math.min(1, 0.6 + size * 0.35), 1.15 - size * 0.25)) explosionSound(s, ev.kind, ev.r, pan);
        break;
      }
      case 'beam_merge':
        if (this.allow(`merge:${Math.min(ev.a, ev.b)}:${Math.max(ev.a, ev.b)}`, MERGE_THROTTLE_MS)) {
          // two beams crossing glance off each other (beamCross.ts): the clash, else the old merge swell
          const pan = this.panAt(ev.x);
          if (!this.s('beam.clash', pan, 1, this.spread()) && !this.s('beam.merge', pan)) mergeSound(s, pan);
        }
        break;
      case 'shock_self':
        // The local buzz also comes through onQueue; one of the two wins the throttle.
        if (ev.p === pid) {
          if (this.allow('shock', SHOCK_THROTTLE_MS) && !this.s('shock.self')) selfShockBuzz(s, 0);
        } else {
          const pan = this.panOf(ev.p, vm);
          if (!this.s('shock.self', pan, 0.5)) selfShockBuzz(s, pan, 0.5);
        }
        break;
      case 'magick': {
        const pan = this.panAt(ev.x);
        if (!this.s(`magick.${ev.id}`, pan)) magickSound(s, ev.id, pan);
        break;
      }
      // Deeper spells (docs/spells-depth.md): the recordings when there are some, a synth stand-in when not.
      case 'weave': {
        const pan = this.panOf(ev.p, vm);
        if (ev.o === 'start') {
          const d = ev.s ?? 0.5;
          if (!this.s('weave.start', pan, 0.7)) s.tone({ freq: 330, glideTo: 660, glideTime: d, dur: d, type: 'triangle', gain: 0.05, pan, release: 0.05 });
        } else if (ev.o === 'fail') {
          if (!this.s('weave.fail', pan, 0.6)) s.tone({ freq: 180, dur: 0.12, type: 'square', gain: 0.04, pan, release: 0.04 });
        } else if (!this.s('weave.break', pan, 0.7)) {
          s.tone({ freq: 260, glideTo: 90, glideTime: 0.2, dur: 0.22, type: 'sawtooth', gain: 0.04, pan, release: 0.05 });
        }
        break;
      }
      case 'woven': {
        const pan = this.panAt(ev.x);
        const name = ev.tier >= 5 ? 'weave.five' : ev.tier === 4 ? 'weave.four' : ev.tier === 3 ? 'weave.three' : 'weave.two';
        if (!this.s(name, pan)) {
          const f = ELEMENT_FREQ[ev.el];
          s.chord([f, f * 1.5, f * 2], { dur: 0.3 + 0.12 * ev.tier, type: 'sine', gain: 0.06 });
        }
        this.s(`voice.${ev.el}`, pan, 0.8);
        break;
      }
      case 'stash': {
        if (ev.o !== 'stashed' && ev.o !== 'pulled') break;
        const pan = this.panOf(ev.p, vm);
        const into = ev.o === 'stashed';
        if (!this.s(into ? 'stash.in' : 'stash.out', pan, 0.7)) s.tone({ freq: into ? 560 : 400, glideTo: into ? 400 : 560, glideTime: 0.08, dur: 0.1, type: 'sine', gain: 0.05, pan, release: 0.04 });
        break;
      }
      case 'grand': {
        const pan = this.panAt(ev.x);
        if (ev.o === 'start') {
          if (!this.s('grand.incant', pan)) s.tone({ freq: 220, glideTo: 880, glideTime: 1.5, dur: 1.5, type: 'triangle', gain: 0.05, pan, release: 0.1 });
        } else if (ev.o === 'cast') {
          if (!this.s(`grand.${ev.id}`, pan)) s.chord([110, 165, 220, 330], { dur: 1.4, type: 'sine', gain: 0.08 });
        } else if (ev.o === 'end') {
          if (!this.s('timestop.end', pan)) s.tone({ freq: 880, glideTo: 220, glideTime: 0.5, dur: 0.5, type: 'triangle', gain: 0.05, pan, release: 0.1 });
        } else if (ev.o === 'break' && !this.s('weave.break', pan, 0.8)) {
          s.tone({ freq: 260, glideTo: 90, glideTime: 0.25, dur: 0.25, type: 'sawtooth', gain: 0.04, pan, release: 0.05 });
        }
        break;
      }
      case 'ward': {
        // a Second Wind or Soul Ward turns the killing blow: a bright heartbeat
        const pan = this.panAt(ev.x);
        if (!this.s('ward', pan)) s.chord([523, 784, 1046], { dur: 0.5, type: 'sine', gain: 0.07 });
        break;
      }
      case 'doom': {
        // a Death mark falls: a low toll
        const pan = this.panAt(ev.x);
        if (!this.s('doom', pan, 0.8)) s.tone({ freq: 110, glideTo: 55, glideTime: 0.6, dur: 0.7, type: 'triangle', gain: 0.08, pan, release: 0.2 });
        break;
      }
      case 'imbue': {
        // The staff takes the spell: a short rising pair of the element's own tone.
        const pan = this.panOf(ev.p, vm);
        if (!this.s('imbue', pan)) for (const el of ev.el.slice(0, 2)) elementPress(s, el, pan);
        break;
      }
      case 'queue_full':
      case 'cancel':
        // Local presses are voiced by onQueue; other players' queue noises stay silent.
        break;
      case 'wave': {
        // an act starting sounds like a wave starting (story music and stings come with the audio batch)
        const phase = ev.phase === 'story' ? 'wave' : ev.phase;
        if (phase === 'countdown') {
          if (!this.s('tick')) waveSound(s, phase);
        } else if (!this.s(`sting.${phase}`)) waveSound(s, phase);
        break;
      }
      case 'bolt': {
        const pan = this.panAt(ev.x);
        if (!this.s('thunder', pan, 1, this.spread())) boltSound(s, pan);
        break;
      }
      case 'boss': {
        const pan = this.panAt(ev.x);
        if (ev.act === 'rage') this.audio.duck(0.6, 1.6);
        if (!this.s(`boss.${ev.act}`, pan)) bossSound(s, ev.act, pan);
        break;
      }
      case 'ff':
        if (ev.by === pid && this.allow('oops', OOPS_THROTTLE_MS) && !this.s('oops')) oopsSound(s);
        break;
      // story mode (slice 2): no synth stand-ins; without the files these stay quiet
      case 'door':
        this.s(ev.open ? (ev.art === 'rune-door' ? 'door.rune' : ev.art === 'portcullis' ? 'portcullis' : 'door.open') : 'door.slam', this.panAt(ev.x));
        break;
      case 'lit': {
        const b = vm.enemies.find((e) => e.id === ev.tgt);
        this.s(ev.on ? 'brazier.catch' : 'brazier.douse', b ? this.panAt(b.rx) : 0, 1, this.spread(), 120);
        break;
      }
      case 'checkpoint':
        this.s('checkpoint', this.panAt(ev.x));
        break;
      case 'say':
        this.s('ui.caption', 0, 1, this.spread(), 400);
        break;
      case 'emerge':
        this.s('spawn.crack', this.panAt(ev.x), 1, this.spread(), 150);
        break;
      case 'ice':
        this.s(`ice.${ev.act}`, this.panAt(ev.x), 1, this.spread(), 250);
        break;
      case 'bramble':
        // the fire spreading; burning out to ash is quieter, the same crackle lower
        this.s('bramble.burn', this.panAt(ev.x), ev.act === 'burn' ? 1 : 0.5, this.spread() * (ev.act === 'burn' ? 1 : 0.8), 350);
        break;
      case 'lava':
        if (ev.act !== 'sink') this.s(ev.act === 'fill' ? 'lava.fill' : 'lava.set', this.panAt(ev.x), 1, this.spread(), 200);
        break;
      case 'fall':
        this.s('fall', this.panAt(ev.x));
        break;
      case 'mire':
        switch (ev.act) {
          case 'flood': this.s('mire.flood', this.panAt(ev.x)); break;
          case 'sink': this.s('mire.sink', this.panAt(ev.x)); break;
          case 'rise': this.s('mire.rise', this.panAt(ev.x)); break;
          case 'strand': this.s('ice.crack', this.panAt(ev.x)); break;
          case 'phase':
            this.audio.duck(0.6, 1.6);
            if (!this.s('boss.rage', this.panAt(ev.x))) bossSound(s, 'rage', this.panAt(ev.x));
            break;
          case 'everflame': if ((ev.v ?? 0) > 0) this.s('everflame', this.panAt(ev.x)); break;
          default: break;
        }
        break;
      case 'flag':
        this.s(`flag.${ev.act}`, this.panAt(ev.x));
        break;
      case 'boiler':
        if (ev.act === 'gauge') this.s('boiler.gauge', this.panAt(ev.x), 1, 1 + 0.04 * (ev.v ?? 0));
        else this.s('boiler.fill', this.panAt(ev.x), ev.act === 'full' ? 0.85 : 1, this.spread(), 400);
        break;
    }
  }

  /** The music that ends a game: a capture-the-flag match won, lost or drawn by your team, else the game-over sting. */
  private endSting(vm: ViewModel): string {
    const c = vm.ctf;
    const mine = vm.players.find((p) => p.isLocal)?.tm;
    if (vm.room?.mode !== 'ctf' || !c || mine === undefined || c.s[0] === c.s[1]) return 'music.gameover';
    return (c.s[0] > c.s[1] ? 0 : 1) === mine ? 'music.victory' : 'music.defeat';
  }

  /** The room phase changed: the music and ambience beds follow it. */
  private onPhase(phase: ViewModel['phase'], vm: ViewModel): void {
    this.phase = phase;
    this.waveN = vm.wave?.n ?? this.waveN;
    if (this.resultsTimer) {
      clearTimeout(this.resultsTimer);
      this.resultsTimer = null;
    }
    switch (phase) {
      case 'wave':
        this.audio.bed('ambience', 'amb.arena');
        this.audio.bed('music', this.waveBed(vm));
        this.audio.duck(0.5, 1.2);
        break;
      case 'story':
        // act 1's two layers in step: the fight layer comes up while the room is fighting
        this.audio.bed('ambience', 'amb.arena');
        this.storyMix = fightMix(vm);
        this.audio.layers('music.cloister-explore', 'music.cloister-fight', this.storyMix);
        this.audio.duck(0.5, 1.2);
        break;
      case 'match':
        // capture the flag: the match's own bed
        this.audio.bed('ambience', 'amb.arena');
        this.audio.bed('music', 'music.ctf');
        this.audio.duck(0.5, 1.2);
        break;
      case 'breather':
        this.audio.duck(0.35, 2.5);
        break;
      case 'countdown':
        break;
      case 'gameover':
        this.audio.bed('ambience', null);
        this.audio.bed('music', this.endSting(vm));
        this.resultsTimer = setTimeout(() => {
          this.resultsTimer = null;
          if (this.phase === 'gameover') this.audio.bed('music', 'music.results');
        }, RESULTS_AFTER_MS);
        break;
      case 'lobby':
        this.audio.bed('ambience', null);
        this.audio.bed('music', 'music.lobby');
        break;
    }
  }

  /**
   * The wave's bed: the jotunn's own on a boss wave (its second phase once it
   * has enraged, and on until the wave ends), else the busier bed from
   * HIGH_WAVE on.
   */
  private waveBed(vm: ViewModel): string {
    if (isBossWave(this.waveN)) {
      if (vm.enemies.some((e) => e.boss && e.ph === 2)) this.raged = true;
      return this.raged ? 'music.boss-rage' : 'music.boss';
    }
    return this.waveN >= HIGH_WAVE ? 'music.wave-high' : 'music.wave-low';
  }

  /** Every frame: sustained sounds and the cues that have no event of their own. */
  onFrame(vm: ViewModel, dt: number): void {
    if (vm.epoch !== this.epoch) {
      // The picture started over: what vanished with the old room wasn't broken, so it makes no sound.
      this.epoch = vm.epoch;
      this.enemyState.clear();
      this.projectilesSeen.clear();
      this.hazardsSeen.clear();
      this.hazardsPrimed = false;
      this.bubbleSeen.clear();
    }
    this.me = vm.me && vm.me.x !== undefined && vm.me.y !== undefined ? { x: vm.me.x, y: vm.me.y } : this.me;
    if (this.phase !== vm.phase) this.onPhase(vm.phase, vm);
    if (vm.wave) this.waveN = vm.wave.n;
    if (vm.phase !== 'wave') this.raged = false;
    else if (isBossWave(this.waveN) && !this.raged && vm.enemies.some((e) => e.boss && e.ph === 2)) this.audio.bed('music', this.waveBed(vm), 2.5);
    if (vm.phase === 'story') {
      // Mother Mire has her own music while she lives; otherwise act 1's two layers
      const boss = vm.enemies.some((e) => e.k === 'mire');
      if (boss !== this.storyBoss) {
        this.storyBoss = boss;
        if (boss) this.audio.bed('music', 'music.mire', 2.5);
        else this.storyMix = -1;
      }
      const mix = fightMix(vm);
      if (!boss && mix !== this.storyMix) {
        this.storyMix = mix;
        this.audio.layers('music.cloister-explore', 'music.cloister-fight', mix);
      }
    } else {
      this.storyBoss = false;
    }
    if (!this.live()) {
      this.stopLoops();
      this.enemyState.clear();
      this.projectilesSeen.clear();
      this.hazardsSeen.clear();
      this.hazardsPrimed = false;
      return;
    }
    this.updateBeam(vm);
    this.updateBurn(vm);
    this.updateCharge(vm);
    this.updateTornado(vm);
    this.updateTimeStop(vm);
    this.updateHazards(vm);
    this.updateStorm(vm);
    this.updateSpray(vm);
    this.updateArc(vm);
    this.updateBubbles(vm);
    this.updateBoiler(vm);
    this.updateTells(vm);
    this.updateSteps(vm, dt);
  }

  /** The boiler (story) roars while it heats: louder the hotter it is, and by distance. */
  private updateBoiler(vm: ViewModel): void {
    const b = vm.enemies.find((e) => e.ob === 'boiler');
    const heat = b && (b.os?.[0] ?? 0) >= 1 ? (b.os?.[1] ?? 0) : 0;
    if (b && heat > 0.05) {
      if (!this.boilerLoop || this.boilerLoop.stopped) {
        this.boilerLoop = this.audio.loop('loop.boiler', { gain: 0.05 })
          ?? this.synth.loop({ kind: 'noise', freq: 220, gain: 0.01, filter: 'lowpass', filterFreq: 400, q: 0.7, tremoloHz: 6, tremoloDepth: 0.3, attack: 0.4 });
      }
      const vol = this.volumeAt(b.rx, b.ry) * (0.25 + 0.75 * heat);
      this.boilerLoop?.setGain(isSample(this.boilerLoop) ? vol : 0.06 * vol);
      this.boilerLoop?.setPan(this.panAt(b.rx));
    } else if (this.boilerLoop) {
      this.boilerLoop.stop(0.8);
      this.boilerLoop = null;
    }
  }

  /** A tornado howls while one is on the field, louder and panned toward the nearest. */
  private updateTornado(vm: ViewModel): void {
    let near: { x: number; y: number } | null = null;
    let best = Infinity;
    for (const m of vm.markers) {
      if (m.k !== 'tornado') continue;
      const d = this.me ? Math.hypot(m.x - this.me.x, m.y - this.me.y) : 0;
      if (d < best) { best = d; near = m; }
    }
    if (near) {
      if (!this.tornadoLoop || this.tornadoLoop.stopped) {
        this.tornadoLoop = this.audio.loop('loop.tornado', { gain: 0.1 })
          ?? this.synth.loop({ kind: 'noise', freq: 600, gain: 0.02, filter: 'bandpass', filterFreq: 600, q: 0.8, tremoloHz: 3, tremoloDepth: 0.4, attack: 0.3 });
      }
      const vol = this.volumeAt(near.x, near.y);
      this.tornadoLoop?.setGain(isSample(this.tornadoLoop) ? 0.8 * vol : 0.12 * vol);
      this.tornadoLoop?.setPan(this.panAt(near.x));
    } else if (this.tornadoLoop) {
      this.tornadoLoop.stop(0.5);
      this.tornadoLoop = null;
    }
  }

  /** While time is stopped (either Time Stop), a slow muffled clock ticks under everything; it fades when time runs again. */
  private updateTimeStop(vm: ViewModel): void {
    const ts = vm.wave?.ts ?? 0;
    if (ts > 0) {
      if (!this.timestopLoop || this.timestopLoop.stopped) this.timestopLoop = this.audio.loop('loop.timestop', { gain: 0 });
      this.timestopLoop?.setGain(Math.min(1, ts / 0.4) * (vm.wave?.tsl ? 0.6 : 1));
    } else if (this.timestopLoop) {
      this.timestopLoop.stop(0.4);
      this.timestopLoop = null;
    }
  }

  /**
   * Shields, walls and mines have no event of their own for coming and going: a mine that appears arms with a
   * click; a shield that vanishes with hp left was broken (it worn down to nothing fades), and a rock or ice wall
   * that vanishes crumbles or shatters. Hazards already there when the session starts make no sound.
   */
  private updateHazards(vm: ViewModel): void {
    const frame = ++this.hazardFrame;
    for (const h of vm.hazards) {
      let seen = this.hazardsSeen.get(h.id);
      if (!seen) {
        seen = { k: h.k, ice: h.el.includes('ice'), frac: 1, x: h.rx, y: h.ry, frame };
        this.hazardsSeen.set(h.id, seen);
        if (this.hazardsPrimed && h.k === 'mine' && this.allow('mine.arm', MINE_ARM_THROTTLE_MS)) this.s('mine.arm', this.panAt(h.rx), this.volumeAt(h.rx, h.ry), this.spread());
      }
      seen.frame = frame;
      seen.x = h.rx;
      seen.y = h.ry;
      seen.frac = h.k === 'barrier' ? h.hp / BARRIER.hp : 1;
    }
    this.hazardsPrimed = true;
    for (const [id, seen] of this.hazardsSeen) {
      if (seen.frame === frame) continue;
      this.hazardsSeen.delete(id);
      const pan = this.panAt(seen.x), vol = this.volumeAt(seen.x, seen.y);
      if (seen.k === 'barrier' && this.allow('shield.gone', WALL_BREAK_THROTTLE_MS)) this.s(seen.frac > 0.08 ? 'shield.break' : 'shield.fade', pan, vol, this.spread());
      else if (seen.k === 'wall' && this.allow('wall.gone', WALL_BREAK_THROTTLE_MS)) this.s(seen.ice ? 'wall.shatter' : 'wall.crumble', pan, vol, this.spread());
    }
  }

  /** A storm wall roars, crackles or hisses in its element while one is near, louder the closer it is. */
  private updateStorm(vm: ViewModel): void {
    let near: (typeof vm.hazards)[number] | null = null;
    let best = STORM_HEAR_UNITS;
    for (const h of vm.hazards) {
      if (h.k !== 'storm') continue;
      const d = this.me ? Math.hypot(h.rx - this.me.x, h.ry - this.me.y) : 0;
      if (d < best) { best = d; near = h; }
    }
    if (!near) {
      if (this.stormLoop) { this.stormLoop.stop(0.6); this.stormLoop = null; this.stormSound = null; }
      return;
    }
    const name = `loop.storm.${STORM_SOUND[leadElement(near.el.filter((e) => e !== 'shield'))]}`;
    if (!this.stormLoop || this.stormLoop.stopped || this.stormSound !== name) {
      this.stormLoop?.stop(0.3);
      this.stormSound = name;
      this.stormLoop = this.audio.loop(name, { gain: 0 });
    }
    this.stormLoop?.setGain(0.7 * Math.max(0, 1 - best / STORM_HEAR_UNITS) * Math.min(1, near.t / 0.5), 0.1);
    this.stormLoop?.setPan(this.panAt(near.rx));
  }

  /**
   * A spray gushes, roars or hisses in its lead element while it lasts: the caster's own at full strength, else the
   * nearest one quieter with distance (one voice for all sprays).
   */
  private updateSpray(vm: ViewModel): void {
    let pick: (typeof vm.sprays)[number] | null = null;
    let best = CHANNEL_HEAR_UNITS;
    for (const s of vm.sprays) {
      if (s.isLocal) { pick = s; best = 0; break; }
      const d = this.me ? Math.hypot(s.rx - this.me.x, s.ry - this.me.y) : 0;
      if (d < best) { best = d; pick = s; }
    }
    const el = pick ? SPRAY_SOUND[leadElement(pick.el)] : null;
    if (!pick || !el) {
      if (this.sprayLoop) { this.sprayLoop.stop(0.15); this.sprayLoop = null; this.spraySound = null; }
      return;
    }
    const name = `loop.spray.${el}`;
    if (!this.sprayLoop || this.sprayLoop.stopped || this.spraySound !== name) {
      this.sprayLoop?.stop(0.1);
      this.spraySound = name;
      this.sprayLoop = this.audio.loop(name, { gain: 0 });
    }
    this.sprayLoop?.setGain(pick.isLocal ? 0.8 : 0.5 * Math.max(0, 1 - best / CHANNEL_HEAR_UNITS), 0.06);
    this.sprayLoop?.setPan(this.panAt(pick.rx));
  }

  /** Chain lightning crackles while an arc holds: the caster's own, else the nearest. */
  private updateArc(vm: ViewModel): void {
    let pick: (typeof vm.arcs)[number] | null = null;
    let best = CHANNEL_HEAR_UNITS;
    for (const a of vm.arcs) {
      const p = a.pts[0];
      if (!p) continue;
      if (a.isLocal) { pick = a; best = 0; break; }
      const d = this.me ? Math.hypot(p[0] - this.me.x, p[1] - this.me.y) : 0;
      if (d < best) { best = d; pick = a; }
    }
    if (!pick) {
      if (this.arcLoop) { this.arcLoop.stop(0.1); this.arcLoop = null; }
      return;
    }
    if (!this.arcLoop || this.arcLoop.stopped) this.arcLoop = this.audio.loop('loop.arc', { gain: 0 });
    this.arcLoop?.setGain(pick.isLocal ? 0.75 : 0.45 * Math.max(0, 1 - best / CHANNEL_HEAR_UNITS), 0.05);
    this.arcLoop?.setPan(this.panAt(pick.pts[0]?.[0]));
  }

  /** A wizard's bubble running out pops. */
  private updateBubbles(vm: ViewModel): void {
    for (const p of vm.players) {
      const was = this.bubbleSeen.get(p.id) ?? 0;
      if (was > 0 && p.bub <= 0 && !p.dead) this.s('bubble.pop', this.panAt(p.rx), this.volumeAt(p.rx, p.ry), this.spread());
      this.bubbleSeen.set(p.id, p.bub);
    }
    if (this.bubbleSeen.size > 16) for (const id of this.bubbleSeen.keys()) if (!vm.players.some((p) => p.id === id)) this.bubbleSeen.delete(id);
  }

  // ---------------------------------------------------------------- sustained voices

  private startBeam(lead: Element, gain: number, pan: number | undefined, remote: boolean): LoopVoice | null {
    const sample = this.audio.loop(remote ? 'loop.beam.remote' : `loop.beam.${lead}`, { gain: remote ? 0.5 : 1, pan: pan ?? 0 })
      ?? (remote ? null : this.audio.loop('loop.beam.remote', { gain: 1, pan: pan ?? 0 }));
    if (sample) return sample;
    if (remote) {
      return this.synth.loop({
        kind: 'tone', freq: ELEMENT_FREQ[lead], type: 'triangle', gain, filter: 'lowpass', filterFreq: 1200, tremoloHz: 6, tremoloDepth: 0.35, attack: 0.1, pan: pan ?? 0,
      });
    }
    // DESIGN 6.8: sustained element tone with a low-pass tremolo while the beam is up.
    return this.synth.loop({
      kind: 'tone', freq: ELEMENT_FREQ[lead], type: lead === 'life' ? 'triangle' : 'sawtooth', gain,
      filter: 'lowpass', filterFreq: ELEMENT_FREQ[lead] * 4, q: 2, tremoloHz: 6, tremoloDepth: 0.35, attack: 0.08,
    });
  }

  private updateBeam(vm: ViewModel): void {
    const local = vm.beams.find((b) => b.isLocal) ?? null;
    if (local) {
      const lead = leadElement(local.el);
      if (!this.beamLoop || this.beamLoop.stopped || this.beamElement !== lead) {
        this.beamLoop?.stop();
        this.beamElement = lead;
        this.beamLoop = this.startBeam(lead, BEAM_GAIN, undefined, false);
      }
      const merged = local.merged === 1;
      if (this.beamLoop) this.beamLoop.setGain(isSample(this.beamLoop) ? (merged ? 1 : 0.7) : (merged ? BEAM_GAIN * 1.6 : BEAM_GAIN));
    } else if (this.beamLoop) {
      this.beamLoop.stop();
      this.beamLoop = null;
      this.beamElement = null;
    }

    const remote = vm.beams.find((b) => !b.isLocal) ?? null;
    if (remote) {
      const lead = leadElement(remote.el);
      if (!this.remoteBeamLoop || this.remoteBeamLoop.stopped) {
        this.remoteBeamElement = lead;
        this.remoteBeamLoop = this.startBeam(lead, REMOTE_BEAM_GAIN, this.panAt(remote.rx), true);
      } else {
        if (!isSample(this.remoteBeamLoop) && lead !== this.remoteBeamElement) this.remoteBeamLoop.setFreq(ELEMENT_FREQ[lead]);
        this.remoteBeamElement = lead;
        this.remoteBeamLoop.setPan(this.panAt(remote.rx));
      }
    } else if (this.remoteBeamLoop) {
      this.remoteBeamLoop.stop();
      this.remoteBeamLoop = null;
    }
  }

  private updateBurn(vm: ViewModel): void {
    const burning = vm.me?.statuses.find((st) => st.id === 'burning') ?? null;
    if (burning && !vm.me?.dead) {
      if (!this.burnLoop || this.burnLoop.stopped) {
        this.burnLoop = this.audio.loop('loop.burn', { gain: 0.6 })
          ?? this.synth.loop({ kind: 'noise', freq: 500, gain: BURN_GAIN, filter: 'bandpass', filterFreq: 500, q: 1.5, tremoloHz: 14, tremoloDepth: 0.7, attack: 0.1 });
      }
      const stacks = Math.min(2, 0.6 + burning.s * 0.25);
      this.burnLoop?.setGain(isSample(this.burnLoop) ? Math.min(1, 0.4 * stacks) : BURN_GAIN * stacks);
    } else if (this.burnLoop) {
      this.burnLoop.stop(0.2);
      this.burnLoop = null;
    }
  }

  private updateCharge(vm: ViewModel): void {
    const charge = vm.me && !vm.me.dead ? vm.me.charge : 0;
    if (charge > 0) {
      const freq = CHARGE_FREQ_MIN + (CHARGE_FREQ_MAX - CHARGE_FREQ_MIN) * charge;
      if (!this.chargeLoop || this.chargeLoop.stopped) {
        this.chargeLoop = this.audio.loop('loop.charge', { gain: 0.3, rate: 0.8 })
          ?? this.synth.loop({ kind: 'tone', freq, type: 'triangle', gain: CHARGE_GAIN_MIN, filter: 'lowpass', filterFreq: 1500, attack: 0.05 });
      } else if (isSample(this.chargeLoop)) {
        this.chargeLoop.setRate(0.8 + charge * 0.7, 0.03);
        this.chargeLoop.setGain(0.3 + charge * 0.7);
      } else {
        this.chargeLoop.setFreq(freq, 0.03);
        this.chargeLoop.setGain(CHARGE_GAIN_MIN + (CHARGE_GAIN_MAX - CHARGE_GAIN_MIN) * charge);
      }
    } else if (this.chargeLoop) {
      this.chargeLoop.stop(0.05);
      this.chargeLoop = null;
    }
  }

  // ---------------------------------------------------------------- cues without events

  /**
   * Enemy tells: the moment an enemy's state turns to attack or channel it
   * makes its noise (the bowstring, the roar, the chant), and a bomb being
   * thrown hisses from where it is. New enemies in a wave announce themselves.
   */
  private updateTells(vm: ViewModel): void {
    const next = new Map<number, string>();
    const inWave = vm.phase === 'wave' || vm.phase === 'story';
    for (const e of vm.enemies) {
      const prev = this.enemyState.get(e.id);
      next.set(e.id, e.a);
      if (e.k === 'dummy') continue;
      const pan = this.panAt(e.rx);
      const vol = this.volumeAt(e.rx, e.ry);
      if (prev === undefined) {
        if (inWave && this.enemyState.size > 0 && this.allow('spawn', SPAWN_THROTTLE_MS)) {
          if (!this.s('spawn', pan, vol * 0.5)) this.synth.noise({ dur: 0.18, filter: 'bandpass', freq: 700, freqTo: 200, q: 1.2, gain: 0.12 * vol, pan });
        }
        continue;
      }
      if (prev !== e.a && (e.a === 'attack' || e.a === 'channel') && this.allow(`tell:${e.id}`, TELL_THROTTLE_MS)) {
        if (!this.s(`tell.${e.k}`, pan, vol, this.spread())) this.synthTell(e.k, pan, vol);
      }
    }
    this.enemyState = next;

    const seen = new Set<number>();
    for (const p of vm.projectiles) {
      seen.add(p.id);
      if (p.k === 'bomb' && !this.projectilesSeen.has(p.id) && this.projectilesSeen.size + seen.size > 1) {
        const pan = this.panAt(p.rx);
        const vol = this.volumeAt(p.rx, p.ry);
        if (!this.s('tell.bomber', pan, vol)) this.synthTell('bomber', pan, vol);
      }
    }
    this.projectilesSeen = seen;
  }

  /** The synth's stand-in tells, one shape per kind, until the recordings land. */
  private synthTell(kind: string, pan: number, vol: number): void {
    const s = this.synth;
    switch (kind) {
      case 'ranger':
        s.noise({ dur: 0.12, filter: 'highpass', freq: 1800, gain: 0.18 * vol, pan });
        s.tone({ freq: 240, glideTo: 90, dur: 0.14, type: 'triangle', gain: 0.08 * vol, pan });
        break;
      case 'troll':
      case 'jotunn':
        s.tone({ freq: kind === 'jotunn' ? 70 : 110, glideTo: 50, dur: 0.5, type: 'sawtooth', gain: 0.16 * vol, lowpass: 600, tremoloHz: 11, tremoloDepth: 0.5, pan });
        break;
      case 'shaman':
        [330, 392, 440].forEach((f, i) => s.tone({ freq: f, dur: 0.3, type: 'sine', gain: 0.07 * vol, pan, at: i * 0.1, reverb: 0.4 }));
        break;
      case 'bomber':
        s.noise({ dur: 0.35, filter: 'bandpass', freq: 3000, q: 2, gain: 0.12 * vol, pan });
        break;
      case 'spider':
        for (let i = 0; i < 3; i++) s.noise({ dur: 0.03, filter: 'highpass', freq: 4000, gain: 0.1 * vol, pan, at: i * 0.05 });
        break;
      case 'skeleton':
        for (let i = 0; i < 3; i++) s.tone({ freq: 900 + i * 120, dur: 0.03, type: 'square', gain: 0.05 * vol, pan, at: i * 0.04 });
        break;
      default:
        s.tone({ freq: 160, glideTo: 110, dur: 0.18, type: 'sawtooth', gain: 0.08 * vol, lowpass: 900, pan });
    }
  }

  /** Footsteps: a tick for every STEP_UNITS the local player walks. */
  private updateSteps(vm: ViewModel, dt: number): void {
    void dt;
    const local = vm.players.find((p) => p.isLocal);
    if (!local || vm.me?.dead) {
      this.lastPos = null;
      return;
    }
    const pos = { x: local.rx, y: local.ry };
    if (this.lastPos) {
      const d = Math.hypot(pos.x - this.lastPos.x, pos.y - this.lastPos.y);
      // A teleport or a respawn is not a stride.
      if (d < 2) this.walked += d;
      if (this.walked >= STEP_UNITS) {
        this.walked = 0;
        if (!this.s('step', 0, 0.35, this.spread())) this.synth.noise({ dur: 0.05, filter: 'lowpass', freq: 500, freqTo: 180, gain: 0.08 });
      }
    }
    this.lastPos = pos;
  }

  private stopLoops(): void {
    this.beamLoop?.stop();
    this.beamLoop = null;
    this.beamElement = null;
    this.remoteBeamLoop?.stop();
    this.remoteBeamLoop = null;
    this.burnLoop?.stop();
    this.burnLoop = null;
    this.chargeLoop?.stop();
    this.chargeLoop = null;
    this.tornadoLoop?.stop();
    this.tornadoLoop = null;
    this.timestopLoop?.stop();
    this.timestopLoop = null;
    this.boilerLoop?.stop();
    this.boilerLoop = null;
    this.stormLoop?.stop();
    this.stormLoop = null;
    this.stormSound = null;
    this.sprayLoop?.stop();
    this.sprayLoop = null;
    this.spraySound = null;
    this.arcLoop?.stop();
    this.arcLoop = null;
  }

  // ---------------------------------------------------------------- helpers

  private live(): boolean {
    return !this.audio.muted && this.synth.ready;
  }

  /** True at most once per `ms` for `key`. */
  private allow(key: string, ms: number): boolean {
    const now = performance.now();
    const last = this.lastAt.get(key);
    if (last !== undefined && now - last < ms) return false;
    this.lastAt.set(key, now);
    if (this.lastAt.size > 512) {
      for (const [k, t] of this.lastAt) if (now - t > 5000) this.lastAt.delete(k);
    }
    return true;
  }

  /** Stereo position of a world x relative to the local player (DESIGN 6.8 "panning by x offset"). */
  private panAt(x: number | undefined): number {
    if (x === undefined || !this.me) return 0;
    return clampPan((x - this.me.x) / PAN_UNITS);
  }

  /** Quieter with distance: full within 6 units, a third at the far side of the arena. */
  private volumeAt(x: number, y: number): number {
    if (!this.me) return 1;
    const d = Math.hypot(x - this.me.x, y - this.me.y);
    return Math.max(0.3, 1 - Math.max(0, d - 6) / 30);
  }

  private panOf(id: number, vm: ViewModel): number {
    const p = vm.players.find((e) => e.id === id);
    if (p) return this.panAt(p.rx);
    const e = vm.enemies.find((en) => en.id === id);
    if (e) return this.panAt(e.rx);
    const c = vm.corpses.find((co) => co.id === id);
    return c ? this.panAt(c.rx) : 0;
  }
}
